// src/chartScale.ts
var MANTISAS = [1, 2, 5];
var EPS = 1e-9;
function decimalesDe(step) {
  if (!Number.isFinite(step) || step <= 0) return 0;
  if (Number.isInteger(step)) return 0;
  const exp = Math.floor(Math.log10(step));
  return exp < 0 ? -exp : 0;
}
function limpiar(v, decimales) {
  const r = Number(v.toFixed(Math.min(20, decimales + 2)));
  return Object.is(r, -0) ? 0 : r;
}
function niceAxis(minY, maxY, maxIntervalos = 10) {
  const lo0 = Number.isFinite(minY) ? Math.min(0, minY) : 0;
  const hi0 = Number.isFinite(maxY) ? Math.max(0, maxY) : 0;
  const hi = lo0 === 0 && hi0 === 0 ? 1 : hi0;
  const lo = lo0;
  const tope = Math.max(1, Math.floor(maxIntervalos));
  for (let exp = -12; exp <= 18; exp++) {
    for (const m of MANTISAS) {
      const step = m * Math.pow(10, exp);
      const max = Math.ceil(hi / step - EPS) * step;
      const min = Math.floor(lo / step + EPS) * step;
      const n = Math.round((max - min) / step);
      if (n >= 1 && n <= tope) {
        const dec = decimalesDe(step);
        const ticks = [];
        for (let i = 0; i <= n; i++) ticks.push(limpiar(min + i * step, dec));
        return { min: limpiar(min, dec), max: limpiar(max, dec), step, ticks };
      }
    }
  }
  return { min: lo, max: hi, step: hi - lo || 1, ticks: [lo, hi] };
}
function aplicarOverridesEje(base, maxValue, stepSize, maxDatos) {
  let max = base.max;
  let step = base.step;
  if (maxValue !== void 0 && Number.isFinite(maxValue) && maxValue >= maxDatos && maxValue > base.min) {
    max = maxValue;
  }
  if (stepSize !== void 0 && Number.isFinite(stepSize) && stepSize > 0 && (max - base.min) / stepSize >= 2 - EPS) {
    step = stepSize;
  }
  if (max === base.max && step === base.step) return base;
  const n = Math.max(1, Math.floor((max - base.min) / step + EPS));
  const nSeguro = Math.min(n, 200);
  const dec = decimalesDe(step);
  const ticks = [];
  for (let i = 0; i <= nSeguro; i++) {
    const v = limpiar(base.min + i * step, dec);
    if (v > max + EPS) break;
    ticks.push(v);
  }
  return { min: base.min, max, step, ticks };
}
function splineCurve(p0, p1, p2, t) {
  const d01 = Math.sqrt((p1[0] - p0[0]) ** 2 + (p1[1] - p0[1]) ** 2);
  const d12 = Math.sqrt((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2);
  const total = d01 + d12;
  const s01 = total > 0 ? d01 / total : 0;
  const s12 = total > 0 ? d12 / total : 0;
  const fa = t * s01;
  const fb = t * s12;
  return {
    prev: [p1[0] - fa * (p2[0] - p0[0]), p1[1] - fa * (p2[1] - p0[1])],
    next: [p1[0] + fb * (p2[0] - p0[0]), p1[1] + fb * (p2[1] - p0[1])]
  };
}
function acotar(v, lo, hi) {
  return v < lo ? lo : v > hi ? hi : v;
}
function pathLinea(pts, tension, caja) {
  if (pts.length === 0) return "";
  const m = (p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`;
  if (pts.length === 1) return `M ${m(pts[0])}`;
  if (!(tension > 0)) {
    return `M ${pts.map(m).join(" L ")}`;
  }
  const n = pts.length;
  const ctrl = [];
  for (let i = 0; i < n; i++) {
    const antes = pts[i - 1] ?? pts[i];
    const despues = pts[i + 1] ?? pts[i];
    const c = splineCurve(antes, pts[i], despues, tension);
    ctrl.push({
      prev: [acotar(c.prev[0], caja.x0, caja.x1), acotar(c.prev[1], caja.y0, caja.y1)],
      next: [acotar(c.next[0], caja.x0, caja.x1), acotar(c.next[1], caja.y0, caja.y1)]
    });
  }
  let d = `M ${m(pts[0])}`;
  for (let i = 0; i < n - 1; i++) {
    d += ` C ${m(ctrl[i].next)} ${m(ctrl[i + 1].prev)} ${m(pts[i + 1])}`;
  }
  return d;
}
function indexAtX(x, n, padL, plotW) {
  if (n <= 1) return 0;
  if (!(plotW > 0)) return 0;
  const i = Math.round((x - padL) * (n - 1) / plotW);
  return Math.max(0, Math.min(n - 1, i));
}
function indexAtXBarras(x, n, padL, plotW) {
  if (n <= 0) return 0;
  if (!(plotW > 0)) return 0;
  const ancho = plotW / n;
  const i = Math.floor((x - padL) / ancho);
  return Math.max(0, Math.min(n - 1, i));
}
function sliceAngles(valores, startAngleGrados = -90) {
  const total = valores.reduce((a, v) => a + (v > 0 ? v : 0), 0);
  if (!(total > 0)) return [];
  let ang = startAngleGrados * Math.PI / 180;
  return valores.map((v) => {
    const a0 = ang;
    const a1 = ang + (v > 0 ? v : 0) / total * 2 * Math.PI;
    ang = a1;
    return { a0, a1 };
  });
}

// src/chartRenderer.ts
function field(it, name) {
  if (it == null || !name) return void 0;
  const g = it.get;
  if (typeof g === "function") {
    try {
      const v = g.call(it, name);
      if (v !== void 0) return v;
    } catch {
    }
  }
  return it[name];
}
var PALETTE = [
  "#2b7400",
  "#1F6BB0",
  "#E0A82E",
  "#B12C82",
  "#7A2EB0",
  "#2E8FD0",
  "#8FBF5A",
  "#7E8B8F",
  "#FF4B0A",
  "#4A4F52",
  "#1FA0B5",
  "#A6A92B"
];
function num(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v ?? "").trim();
  if (s === "") return 0;
  const n = Number(s);
  if (Number.isFinite(n)) return n;
  const n2 = parseFloat(s.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n2) ? n2 : 0;
}
function esc(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function isColor(s) {
  return /^#([0-9a-fA-F]{3,8})$/.test(s.trim());
}
function fmt(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(".", ",");
}
function fmtTick(v, dec) {
  if (dec === 0 || v === 0) return String(Math.round(v));
  return v.toFixed(dec).replace(".", ",");
}
var RESIZE_UMBRAL = 2;
var MIN_PX_POR_TICK = 12;
var ultimaHuella = /* @__PURE__ */ new WeakMap();
var ultimaCaja = /* @__PURE__ */ new WeakMap();
var observadores = /* @__PURE__ */ new WeakMap();
var ultimoRender = /* @__PURE__ */ new WeakMap();
function svgPlotBox(container, reserveLegend) {
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  if (cw >= 60 && ch >= 60) {
    const legendH = reserveLegend ? 24 : 0;
    return { w: Math.max(120, cw - 8), h: Math.max(80, ch - 8 - legendH) };
  }
  return { w: 320, h: 170 };
}
function polar(cx, cy, r, a) {
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}
function slicePath(cx, cy, R, inner, a0, a1) {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = polar(cx, cy, R, a0);
  const [x1, y1] = polar(cx, cy, R, a1);
  if (inner <= 0) {
    return `M ${cx} ${cy} L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
  }
  const [ix1, iy1] = polar(cx, cy, inner, a1);
  const [ix0, iy0] = polar(cx, cy, inner, a0);
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} L ${ix1.toFixed(2)} ${iy1.toFixed(2)} A ${inner} ${inner} 0 ${large} 0 ${ix0.toFixed(2)} ${iy0.toFixed(2)} Z`;
}
function legendHtml(entries, extraClass = "") {
  const rows = entries.map(
    (e) => `<li class="xone-chart-legend-item"><span class="xone-chart-swatch" style="background:${esc(e.color)}"></span><span class="xone-chart-legend-label">${esc(e.title)}</span>${e.value !== void 0 ? `<span class="xone-chart-legend-value">${esc(e.value)}</span>` : ""}</li>`
  ).join("");
  const cls = extraClass !== "" ? ` ${extraClass}` : "";
  return `<ul class="xone-chart-legend${cls}">${rows}</ul>`;
}
function crearTip(doc) {
  const tip = doc.createElement("div");
  tip.className = "xone-chart-tip";
  tip.setAttribute("role", "status");
  tip.setAttribute("aria-live", "polite");
  tip.hidden = true;
  return tip;
}
function pintarTip(tip, titulo, filas) {
  const doc = tip.ownerDocument;
  tip.textContent = "";
  if (titulo !== "") {
    const h = doc.createElement("div");
    h.className = "xone-chart-tip-title";
    h.textContent = titulo;
    tip.appendChild(h);
  }
  for (const f of filas) {
    const fila = doc.createElement("div");
    fila.className = "xone-chart-tip-row";
    const sw = doc.createElement("span");
    sw.className = "xone-chart-swatch";
    sw.style.background = f.color;
    const nom = doc.createElement("span");
    nom.className = "xone-chart-tip-label";
    nom.textContent = f.titulo;
    const val = doc.createElement("span");
    val.className = "xone-chart-tip-value";
    val.textContent = f.valor;
    fila.append(sw, nom, val);
    tip.appendChild(fila);
  }
  tip.hidden = false;
}
function colocarTip(tip, container, xRel, yRel) {
  const cw = container.clientWidth || 0;
  const ch = container.clientHeight || 0;
  const tw = tip.offsetWidth || 0;
  const th = tip.offsetHeight || 0;
  const margen = 10;
  let x = xRel + margen;
  let y = yRel + margen;
  if (cw > 0 && x + tw > cw) x = Math.max(0, xRel - tw - margen);
  if (ch > 0 && y + th > ch) y = Math.max(0, yRel - th - margen);
  tip.style.left = `${Math.max(0, x)}px`;
  tip.style.top = `${Math.max(0, y)}px`;
}
function aViewBox(svg, clientX, clientY) {
  const ctm = typeof svg.getScreenCTM === "function" ? svg.getScreenCTM() : null;
  if (!ctm) return null;
  try {
    const inv = ctm.inverse();
    const G = globalThis;
    if (typeof G.DOMPoint === "function") {
      const p = new G.DOMPoint(clientX, clientY).matrixTransform(inv);
      return { x: p.x, y: p.y };
    }
    const legacy = svg;
    if (typeof legacy.createSVGPoint === "function") {
      const p = legacy.createSVGPoint();
      p.x = clientX;
      p.y = clientY;
      return p.matrixTransform(inv);
    }
  } catch {
  }
  return null;
}
function renderPie(container, cfg, items, animar) {
  const valueField = cfg.valueField ?? "";
  const slices = items.map((it, i) => {
    const colorRaw = cfg.colorField ? String(field(it, cfg.colorField) ?? "").trim() : "";
    return {
      label: cfg.labelField ? String(field(it, cfg.labelField) ?? "") : `#${i + 1}`,
      value: num(field(it, valueField)),
      color: isColor(colorRaw) ? colorRaw : PALETTE[i % PALETTE.length]
    };
  }).filter((s) => s.value > 0);
  const total = slices.reduce((a, s) => a + s.value, 0);
  const R = 54, cx = 60, cy = 60, inner = cfg.type === "ring" ? 30 : 0;
  const angs = sliceAngles(slices.map((s) => s.value), cfg.startAngle ?? -90);
  let body = "";
  if (total <= 0) {
    body = `<circle cx="${cx}" cy="${cy}" r="${R}" fill="none" stroke="#E0E0E0" stroke-width="2"/>`;
  } else if (slices.length === 1) {
    const attrs = `class="xone-chart-slice" data-i="0"`;
    body = inner > 0 ? `<circle ${attrs} cx="${cx}" cy="${cy}" r="${(R + inner) / 2}" fill="none" stroke="${esc(slices[0].color)}" stroke-width="${R - inner}"/>` : `<circle ${attrs} cx="${cx}" cy="${cy}" r="${R}" fill="${esc(slices[0].color)}"/>`;
  } else {
    body = slices.map((s, i) => {
      const a = angs[i];
      return `<path class="xone-chart-slice" data-i="${i}" d="${slicePath(cx, cy, R, inner, a.a0, a.a1)}" fill="${esc(s.color)}"/>`;
    }).join("");
  }
  const resumen = total > 0 ? `Gr\xE1fico de sectores con ${slices.length} porciones. ` + slices.map((s) => `${s.label}: ${fmt(s.value)}`).join(", ") : "Gr\xE1fico de sectores sin datos";
  const clsAnim = animar ? " xone-chart-anim" : "";
  const interactivo = (cfg.tooltip ?? "index") !== "none" && total > 0;
  const foco = interactivo ? ` tabindex="0"` : "";
  const svg = `<svg viewBox="0 0 120 120" preserveAspectRatio="xMidYMid meet" role="img"${foco} aria-label="${esc(resumen)}" class="xone-chart-svg xone-chart-pie${clsAnim}"><g class="xone-chart-pie-rot">${body}</g></svg>`;
  const legend = cfg.showLegend !== false ? legendHtml(
    slices.map((s) => ({ title: s.label, color: s.color, value: fmt(s.value) })),
    "xone-chart-legend-overlay"
  ) : "";
  container.innerHTML = `${svg}${legend}`;
  if (!interactivo) return;
  const el = container.querySelector("svg");
  if (!el) return;
  const tip = crearTip(container.ownerDocument);
  container.appendChild(tip);
  wireTooltipPie(container, el, tip, slices, total);
}
function wireTooltipPie(container, svg, tip, slices, total) {
  let activo = -1;
  const mostrar = (i, clientX, clientY) => {
    const s = slices[i];
    if (!s) return;
    activo = i;
    const pct = (s.value / total * 100).toFixed(1).replace(".", ",");
    pintarTip(tip, s.label, [{ titulo: "Total", valor: `${fmt(s.value)} (${pct}%)`, color: s.color }]);
    const caja = container.getBoundingClientRect?.();
    if (caja) colocarTip(tip, container, clientX - caja.left, clientY - caja.top);
    for (const p of svg.querySelectorAll(".xone-chart-slice")) {
      p.classList.toggle("is-activa", Number(p.getAttribute("data-i")) === i);
    }
  };
  const ocultar = () => {
    activo = -1;
    tip.hidden = true;
    for (const p of svg.querySelectorAll(".xone-chart-slice")) p.classList.remove("is-activa");
  };
  svg.addEventListener("pointermove", (ev) => {
    const t = ev.target;
    const i = t?.classList?.contains("xone-chart-slice") ? Number(t.getAttribute("data-i")) : -1;
    if (i >= 0) mostrar(i, ev.clientX, ev.clientY);
    else ocultar();
  });
  svg.addEventListener("pointerleave", ocultar);
  svg.addEventListener("keydown", (ev) => {
    const k = ev.key;
    if (k === "Escape") {
      ocultar();
      return;
    }
    const paso = k === "ArrowRight" ? 1 : k === "ArrowLeft" ? -1 : 0;
    if (paso === 0) return;
    ev.preventDefault();
    const i = activo < 0 ? 0 : (activo + paso + slices.length) % slices.length;
    const caja = container.getBoundingClientRect?.();
    mostrar(i, (caja?.left ?? 0) + (container.clientWidth || 0) / 2, (caja?.top ?? 0) + 8);
  });
  svg.addEventListener("blur", ocultar);
}
function prepararCartesiano(container, cfg, items, esBarra) {
  const xs = items.map((it, i) => cfg.xField ? String(field(it, cfg.xField) ?? "") : String(i + 1));
  const series = (cfg.series ?? []).map((s, si) => ({
    title: s.title,
    color: isColor(s.color) ? s.color : PALETTE[si % PALETTE.length],
    points: items.map((it) => num(field(it, s.field)))
  }));
  let maxDatos = 0, minDatos = 0, hayDatos = false;
  for (const s of series) {
    for (const v of s.points) {
      if (!hayDatos) {
        maxDatos = minDatos = v;
        hayDatos = true;
      } else {
        if (v > maxDatos) maxDatos = v;
        if (v < minDatos) minDatos = v;
      }
    }
  }
  const eje = aplicarOverridesEje(
    niceAxis(minDatos, maxDatos),
    cfg.axisMaxValue,
    cfg.axisStepSize,
    maxDatos
  );
  const showLegend = cfg.showLegend === true && series.length > (esBarra ? 1 : 0);
  const { w: W, h: H } = svgPlotBox(container, showLegend);
  const padL = 32, padR = 10, padT = 10, padB = 26;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const rango = eje.max - eje.min || 1;
  const yOf = (v) => padT + plotH * (1 - (v - eje.min) / rango);
  return { xs, series, eje, W, H, padL, padR, padT, padB, plotW, plotH, yOf, showLegend };
}
function gridYSvg(c) {
  const dec = decimalesDe(c.eje.step);
  const nTicks = c.eje.ticks.length;
  const cada = c.plotH / Math.max(1, nTicks - 1) < MIN_PX_POR_TICK ? 2 : 1;
  let out = "";
  for (let t = 0; t < nTicks; t++) {
    const v = c.eje.ticks[t];
    const y = c.yOf(v);
    out += `<line x1="${c.padL}" y1="${y.toFixed(1)}" x2="${c.W - c.padR}" y2="${y.toFixed(1)}" stroke="#ECECEC" stroke-width="1"/>`;
    if (t % cada === 0) {
      out += `<text x="${c.padL - 4}" y="${(y + 3).toFixed(1)}" text-anchor="end" class="xone-chart-axis">${esc(fmtTick(v, dec))}</text>`;
    }
  }
  return out;
}
function etiquetasXSvg(c, xOf) {
  const n = c.xs.length;
  const paso = Math.max(1, Math.ceil(n / 8));
  let out = "";
  for (let i = 0; i < n; i += paso) {
    out += `<text x="${xOf(i).toFixed(1)}" y="${c.H - c.padB + 14}" text-anchor="middle" class="xone-chart-axis">${esc(c.xs[i] ?? "")}</text>`;
  }
  return out;
}
function esInteractivo(cfg, c) {
  return (cfg.tooltip ?? "index") !== "none" && c.series.length > 0 && c.xs.length > 0;
}
function resumenCartesiano(tipo, c) {
  if (c.series.length === 0) return `Gr\xE1fico de ${tipo} sin series`;
  return `Gr\xE1fico de ${tipo} con ${c.series.length} serie(s) y ${c.xs.length} punto(s). Series: ${c.series.map((s) => s.title).join(", ")}. Eje de ${fmt(c.eje.min)} a ${fmt(c.eje.max)}.`;
}
function renderLine(container, cfg, items, animar) {
  const c = prepararCartesiano(container, cfg, items, false);
  const n = items.length;
  const xOf = (i) => n > 1 ? c.padL + i * c.plotW / (n - 1) : c.padL + c.plotW / 2;
  const tension = cfg.lineTension ?? 0.4;
  const caja = { x0: c.padL, y0: c.padT, x1: c.W - c.padR, y1: c.padT + c.plotH };
  const lines = c.series.map((s, si) => {
    const pts = s.points.map((v, i) => [xOf(i), c.yOf(v)]);
    const d = pathLinea(pts, tension, caja);
    const dots = pts.map(([x, y], i) => `<circle class="xone-chart-dot" data-s="${si}" data-i="${i}" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${esc(s.color)}"/>`).join("");
    return `<g class="xone-chart-serie" data-s="${si}"><path class="xone-chart-line-path" pathLength="1" d="${d}" fill="none" stroke="${esc(s.color)}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>${dots}</g>`;
  }).reverse().join("");
  const yCero = c.yOf(Math.max(c.eje.min, 0));
  const axis = `<line x1="${c.padL}" y1="${c.padT}" x2="${c.padL}" y2="${c.padT + c.plotH}" stroke="#CFCFCF" stroke-width="1"/><line x1="${c.padL}" y1="${yCero.toFixed(1)}" x2="${c.W - c.padR}" y2="${yCero.toFixed(1)}" stroke="#CFCFCF" stroke-width="1"/>`;
  const cursor = `<g class="xone-chart-cursor" hidden><line class="xone-chart-crosshair" y1="${c.padT}" y2="${c.padT + c.plotH}" stroke="#B0B0B0" stroke-width="1" stroke-dasharray="3 3"/></g>`;
  const clsAnim = animar ? " xone-chart-anim" : "";
  const foco = esInteractivo(cfg, c) ? ` tabindex="0"` : "";
  const svg = `<svg viewBox="0 0 ${c.W} ${c.H}" preserveAspectRatio="xMidYMid meet" role="img"${foco} aria-label="${esc(resumenCartesiano("l\xEDneas", c))}" class="xone-chart-svg xone-chart-line${clsAnim}">${gridYSvg(c)}${axis}${lines}${cursor}${etiquetasXSvg(c, xOf)}</svg>`;
  const legend = c.showLegend ? legendHtml(c.series.map((s) => ({ title: s.title, color: s.color }))) : "";
  container.innerHTML = `${svg}${legend}`;
  wireTooltipCartesiano(container, cfg, c, xOf, false);
}
function renderBar(container, cfg, items, animar) {
  const c = prepararCartesiano(container, cfg, items, true);
  const n = items.length || 1;
  const groupW = c.plotW / n;
  const barW = Math.max(2, groupW * 0.8 / Math.max(1, c.series.length));
  const yCero = c.yOf(Math.max(c.eje.min, 0));
  let bars = "";
  for (let i = 0; i < items.length; i++) {
    const gx = c.padL + i * groupW + groupW * 0.1;
    c.series.forEach((s, si) => {
      const v = s.points[i] ?? 0;
      const y = c.yOf(v);
      const y0 = Math.min(y, yCero), h = Math.abs(yCero - y);
      bars += `<rect class="xone-chart-bar-rect" data-s="${si}" data-i="${i}" x="${(gx + si * barW).toFixed(1)}" y="${y0.toFixed(1)}" width="${barW.toFixed(1)}" height="${h.toFixed(1)}" fill="${esc(s.color)}" style="transform-origin:0 ${yCero.toFixed(1)}px"/>`;
    });
  }
  const xOf = (i) => c.padL + i * groupW + groupW / 2;
  const axis = `<line x1="${c.padL}" y1="${yCero.toFixed(1)}" x2="${c.W - c.padR}" y2="${yCero.toFixed(1)}" stroke="#CFCFCF" stroke-width="1"/>`;
  const clsAnim = animar ? " xone-chart-anim" : "";
  const foco = esInteractivo(cfg, c) ? ` tabindex="0"` : "";
  const svg = `<svg viewBox="0 0 ${c.W} ${c.H}" preserveAspectRatio="xMidYMid meet" role="img"${foco} aria-label="${esc(resumenCartesiano("barras", c))}" class="xone-chart-svg xone-chart-bar${clsAnim}">${gridYSvg(c)}${axis}<g class="xone-chart-bars">${bars}</g>${etiquetasXSvg(c, xOf)}</svg>`;
  const legend = c.showLegend ? legendHtml(c.series.map((s) => ({ title: s.title, color: s.color }))) : "";
  container.innerHTML = `${svg}${legend}`;
  wireTooltipCartesiano(container, cfg, c, xOf, true);
}
function wireTooltipCartesiano(container, cfg, c, xOf, esBarra) {
  const modo = cfg.tooltip ?? "index";
  if (!esInteractivo(cfg, c)) return;
  const svg = container.querySelector("svg");
  if (!svg) return;
  const tip = crearTip(container.ownerDocument);
  container.appendChild(tip);
  const cursor = svg.querySelector(".xone-chart-cursor");
  const cross = svg.querySelector(".xone-chart-crosshair");
  let activo = -1;
  const mostrar = (i, clientX, clientY) => {
    if (i < 0 || i >= c.xs.length) return;
    activo = i;
    let filas = c.series.map((s) => ({ titulo: s.title, valor: fmt(s.points[i] ?? 0), color: s.color }));
    if (modo === "nearest" && filas.length > 1) {
      const caja2 = container.getBoundingClientRect?.();
      const p = caja2 ? aViewBox(svg, clientX, clientY) : null;
      if (p) {
        let mejor = 0, dist = Infinity;
        c.series.forEach((s, si) => {
          const d = Math.abs(c.yOf(s.points[i] ?? 0) - p.y);
          if (d < dist) {
            dist = d;
            mejor = si;
          }
        });
        filas = [filas[mejor]];
      }
    }
    pintarTip(tip, c.xs[i] ?? "", filas);
    const caja = container.getBoundingClientRect?.();
    if (caja) colocarTip(tip, container, clientX - caja.left, clientY - caja.top);
    if (cursor && cross) {
      cross.setAttribute("x1", String(xOf(i)));
      cross.setAttribute("x2", String(xOf(i)));
      cursor.removeAttribute("hidden");
    }
    for (const d of svg.querySelectorAll(".xone-chart-dot, .xone-chart-bar-rect")) {
      d.classList.toggle("is-activo", Number(d.getAttribute("data-i")) === i);
    }
  };
  const ocultar = () => {
    activo = -1;
    tip.hidden = true;
    cursor?.setAttribute("hidden", "");
    for (const d of svg.querySelectorAll(".xone-chart-dot, .xone-chart-bar-rect")) d.classList.remove("is-activo");
  };
  svg.addEventListener("pointermove", (ev) => {
    const p = aViewBox(svg, ev.clientX, ev.clientY);
    if (!p) return;
    const i = esBarra ? indexAtXBarras(p.x, c.xs.length, c.padL, c.plotW) : indexAtX(p.x, c.xs.length, c.padL, c.plotW);
    mostrar(i, ev.clientX, ev.clientY);
  });
  svg.addEventListener("pointerleave", ocultar);
  svg.addEventListener("blur", ocultar);
  svg.addEventListener("keydown", (ev) => {
    const k = ev.key;
    if (k === "Escape") {
      ocultar();
      return;
    }
    const paso = k === "ArrowRight" ? 1 : k === "ArrowLeft" ? -1 : 0;
    if (paso === 0) return;
    ev.preventDefault();
    const i = activo < 0 ? 0 : Math.max(0, Math.min(c.xs.length - 1, activo + paso));
    const caja = container.getBoundingClientRect?.();
    mostrar(i, (caja?.left ?? 0) + xOf(i), (caja?.top ?? 0) + c.padT);
  });
}
function huella(cfg, items) {
  const campos = [
    cfg.labelField ?? "",
    cfg.valueField ?? "",
    cfg.colorField ?? "",
    cfg.xField ?? "",
    ...(cfg.series ?? []).map((s) => s.field)
  ].filter((f) => f !== "");
  const filas = items.map((it) => campos.map((f) => String(field(it, f) ?? "")).join(""));
  return `${cfg.type}|${campos.join(",")}|${filas.join("")}`;
}
function wireResize(container) {
  if (typeof ResizeObserver === "undefined" || observadores.has(container)) return;
  const ro = new ResizeObserver(() => {
    const prev = ultimaCaja.get(container);
    const w = container.clientWidth, h = container.clientHeight;
    if (!prev || Math.abs(prev.w - w) < RESIZE_UMBRAL && Math.abs(prev.h - h) < RESIZE_UMBRAL) return;
    const ultimo = ultimoRender.get(container);
    if (ultimo) renderChart(container, ultimo.cfg, ultimo.items);
  });
  ro.observe(container);
  observadores.set(container, ro);
}
function renderChart(container, config, items) {
  container.classList.add("xone-chart");
  if (config.engine === "chartjs") {
    void import("./chartRendererChartjs-JIDKZU5S.js").then((m) => {
      void m.renderChartJs(container, config, items);
    });
    return;
  }
  const esPie = config.type === "pie" || config.type === "ring";
  container.classList.toggle("xone-chart-row", esPie);
  container.classList.toggle("xone-chart-col", !esPie);
  const h = huella(config, items);
  const animar = config.animate !== false && ultimaHuella.get(container) !== h;
  ultimaHuella.set(container, h);
  ultimoRender.set(container, { cfg: config, items });
  if (esPie) renderPie(container, config, items, animar);
  else if (config.type === "bar") renderBar(container, config, items, animar);
  else renderLine(container, config, items, animar);
  ultimaCaja.set(container, { w: container.clientWidth, h: container.clientHeight });
  wireResize(container);
}

export {
  field,
  PALETTE,
  num,
  isColor,
  renderChart
};
//# sourceMappingURL=chunk-NSXINY2I.js.map